import React, { FC, ReactNode } from "react";
import { styled } from "@mui/system";
import { COLORS, FONT_FAMILIES, FONT_WEIGHTS, RADII, SPACINGS } from "../../common/consts";
import { BlockBone, PillBone, TextBone } from "../Display/Skeletons";
import { HUBSPOT_TILE } from "../HubSpotUi/theme";

/**
 * The settings vocabulary, ported from Outreach's own settings panel (the one
 * Dial faster injects its rows into): a flat white panel, a heading, then rows
 * of label and caption on the left with the control pinned to the far right,
 * separated by 1px rules. Every settings section in the dashboard is built
 * from these so they all line up the same way.
 */

const PanelBase = styled("section")`
  background-color: var(--color-backgroundWhite, ${COLORS.backgroundWhite});
  border-radius: ${RADII.md};
  box-shadow: rgba(69, 66, 98, 0.16) 0 0 1px, rgba(69, 66, 98, 0.16) 0 1px 2px;
  padding: ${SPACINGS.xl};
  max-width: 900px;
  box-sizing: border-box;
  ${HUBSPOT_TILE}
`;

const TitleBase = styled("h3")`
  font-family: ${FONT_FAMILIES.titles};
  font-size: 1rem;
  font-weight: ${FONT_WEIGHTS.semibold};
  line-height: 1.5rem;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
  margin: 0;
`;

const CaptionBase = styled("p")`
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  margin: 0;
`;


/* The class names let the HubSpot settings layout (styles/hubspot.css) lay
   these out as HubSpot's flat settings form; they style nothing on their own. */
const withClass = (name: string, className?: string) => (className ? `${name} ${className}` : name);

export const SettingsPanel: FC<{ className?: string } & React.HTMLAttributes<HTMLElement>> = ({ className, ...rest }) => <PanelBase className={withClass("boring-settings-panel", className)} {...rest} />;
export const SettingsTitle: FC<{ className?: string } & React.HTMLAttributes<HTMLHeadingElement>> = ({ className, ...rest }) => <TitleBase className={withClass("boring-settings-title", className)} {...rest} />;
export const SettingsCaption: FC<{ className?: string } & React.HTMLAttributes<HTMLParagraphElement>> = ({ className, ...rest }) => <CaptionBase className={withClass("boring-settings-caption", className)} {...rest} />;
export const SettingsGroup: FC<{ className?: string } & React.HTMLAttributes<HTMLDivElement>> = ({ className, ...rest }) => <GroupBase className={withClass("boring-settings-group", className)} {...rest} />;

const HeaderWrap = styled("header")`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACINGS.xl};
  padding-bottom: ${SPACINGS.md};

  /* The title and caption take the row, so a switch beside them never
     squeezes the caption into a narrow column. */
  > :first-child {
    flex: 1 1 auto;
    min-width: 0;
  }

  /* width: auto, since a Toggle's wrapper is a full-width flex row. */
  > :last-child:not(:first-child) {
    flex: 0 0 auto;
    width: auto;
    margin-left: auto;
  }
`;

interface HeaderProps {
  title: ReactNode;
  caption?: ReactNode;
  /** Sits at the far right of the heading, for a section-wide switch. */
  control?: ReactNode;
}

export const SettingsHeader: FC<HeaderProps> = ({ title, caption, control }) => (
  <HeaderWrap className="boring-settings-header">
    <div>
      <SettingsTitle>{title}</SettingsTitle>
      {caption && <SettingsCaption>{caption}</SettingsCaption>}
    </div>
    {control}
  </HeaderWrap>
);

/** Rows stacked with a rule between each pair, like Outreach's dividers. */
const GroupBase = styled("div")`
  display: flex;
  flex-direction: column;

  > * + * {
    border-top: 1px solid var(--color-backgroundBrand, ${COLORS.backgroundBrand});
  }
`;

const RowWrap = styled("div")`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.xl};
  padding: ${SPACINGS.md} 0;
  min-height: 3rem;
  box-sizing: border-box;
`;

const RowText = styled("div")`
  flex: 1 1 auto;
  min-width: 0;
`;

const RowLabel = styled("label")`
  display: block;
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

const RowCaption = styled("span")`
  display: block;
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  margin-top: 2px;
`;

/* A field that belongs to the row, shown under the caption once the row's
   switch allows it. This is where Outreach puts "Seconds" under Call delay. */
const RowDetail = styled("div")`
  margin-top: ${SPACINGS.lg};
  max-width: 220px;
`;

const RowControl = styled("div")`
  flex: 0 0 auto;
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

interface RowProps {
  label: ReactNode;
  caption?: ReactNode;
  /** Pinned to the far right: a switch, a select, a button. */
  control?: ReactNode;
  /** Shown under the caption, on the left, for the row's own field. */
  children?: ReactNode;
}

export const SettingsRow: FC<RowProps> = ({ label, caption, control, children }) => (
  <RowWrap className="boring-settings-row">
    <RowText className="boring-settings-row__text">
      <RowLabel className="boring-settings-row__label">{label}</RowLabel>
      {caption && <RowCaption className="boring-settings-row__caption">{caption}</RowCaption>}
      {children && <RowDetail className="boring-settings-row__detail">{children}</RowDetail>}
    </RowText>
    {control && <RowControl className="boring-settings-row__control">{control}</RowControl>}
  </RowWrap>
);

const FooterWrap = styled("footer")`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.md};
  padding-top: ${SPACINGS.lg};
  border-top: 1px solid var(--color-backgroundBrand, ${COLORS.backgroundBrand});
`;

const FooterStart = styled("div")`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${SPACINGS.md};
`;

const FooterActions = styled("div")`
  flex: 0 0 auto;
  margin-left: auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${SPACINGS.sm};
`;

interface FooterProps {
  /** Sits at the left of the row: a status line such as "Saved.". */
  start?: ReactNode;
  /** The buttons, secondary first and Save last, so Save is the rightmost. */
  children?: ReactNode;
  className?: string;
}

/**
 * Save and its neighbours, on their own row under the rows and always at the
 * right edge of the card, the way Outreach puts Cancel and Save.
 */
export const SettingsFooter: FC<FooterProps> = ({ start, children, className }) => (
  <FooterWrap className={className}>
    {start && <FooterStart>{start}</FooterStart>}
    <FooterActions>{children}</FooterActions>
  </FooterWrap>
);

/** A right-hand control that is a select or an input, sized like Outreach's. */
export const SettingsControlField = styled("div")`
  width: 15rem;
`;

/* ------------------------------------------------------------------ */
/* The same vocabulary, in bones, for while the section is loading.    */
/* ------------------------------------------------------------------ */

/** What sits at the right of a row, so the bone is the size of the real one. */
export type SettingsControlKind = "toggle" | "field" | "button" | "none";

const controlBone = (kind: SettingsControlKind): ReactNode => {
  if (kind === "toggle") return <PillBone width={48} height={24} />;
  if (kind === "field") return <BlockBone width="15rem" height={40} />;
  if (kind === "button") return <BlockBone width={104} height={36} radius={RADII.full} />;
  return null;
};

export interface SettingsRowSkeletonProps {
  labelWidth?: number | string;
  /** Omit for a row that carries no caption under its label. */
  captionWidth?: number | string;
  control?: SettingsControlKind;
}

/** One settings row: label and caption on the left, control at the far right. */
export const SettingsRowSkeleton: FC<SettingsRowSkeletonProps> = ({
  labelWidth = 220,
  captionWidth,
  control = "toggle",
}) => (
  <RowWrap>
    <RowText>
      <TextBone width={labelWidth} size="0.875rem" />
      {captionWidth && <TextBone width={captionWidth} size="0.75rem" style={{ marginTop: 2 }} />}
    </RowText>
    {control !== "none" && <RowControl>{controlBone(control)}</RowControl>}
  </RowWrap>
);

export interface SettingsHeaderSkeletonProps {
  titleWidth?: number | string;
  /** Caption lines under the title, at the widths the real caption wraps to. */
  captionWidths?: (number | string)[];
  /** The section-wide control at the right of the heading. */
  control?: SettingsControlKind;
}

/** A section heading: title, caption, and the switch at the far right. */
export const SettingsHeaderSkeleton: FC<SettingsHeaderSkeletonProps> = ({
  titleWidth = 200,
  captionWidths = ["60%"],
  control = "none",
}) => (
  <HeaderWrap>
    <div style={{ flex: "1 1 auto" }}>
      <TextBone width={titleWidth} size="1rem" />
      {captionWidths.map((width, i) => (
        <TextBone key={i} width={width} size="0.875rem" />
      ))}
    </div>
    {control !== "none" && controlBone(control)}
  </HeaderWrap>
);

export interface SettingsPanelSkeletonProps {
  titleWidth?: number | string;
  /** Caption lines under the title, at the widths the real caption wraps to. */
  captionWidths?: (number | string)[];
  /** The section-wide control at the right of the heading. */
  headerControl?: SettingsControlKind;
  /** One entry per row the section renders. */
  rows?: SettingsRowSkeletonProps[];
  /** The Save row under the rules. */
  footer?: boolean;
  /** Width of each button in that footer, in the order they are rendered. */
  footerButtons?: number[];
  className?: string;
}

/**
 * A settings section while its settings are still being fetched: the panel,
 * its heading, a row per setting at the row's own 3rem height, and the footer
 * the Save button lands in.
 */
export const SettingsPanelSkeleton: FC<SettingsPanelSkeletonProps> = ({
  titleWidth = 200,
  captionWidths = ["60%"],
  headerControl = "none",
  rows = [{}, {}, {}],
  footer = true,
  footerButtons = [88],
  className,
}) => (
  <SettingsPanel className={className}>
    <SettingsHeaderSkeleton
      titleWidth={titleWidth}
      captionWidths={captionWidths}
      control={headerControl}
    />
    <SettingsGroup>
      {rows.map((row, i) => (
        <SettingsRowSkeleton key={i} {...row} />
      ))}
    </SettingsGroup>
    {footer && (
      <SettingsFooter>
        {footerButtons.map((width, i) => (
          <BlockBone key={i} width={width} height={36} radius={RADII.full} />
        ))}
      </SettingsFooter>
    )}
  </SettingsPanel>
);
